import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Vector3,
} from "three";

import { latLngToVec3 } from "./geo";

// ============================================================
//  GeoJSON line overlays — timezone borders, country borders.
//  Every polyline becomes pairs in one LineSegments draw call,
//  floated a hair above the surface so it never z-fights.
// ============================================================

export interface GeoJSON {
  features: GeoFeature[];
}
interface GeoFeature {
  geometry:
    | { type: "LineString"; coordinates: number[][] }
    | { type: "MultiLineString"; coordinates: number[][][] }
    | { type: "Polygon"; coordinates: number[][][] }
    | { type: "MultiPolygon"; coordinates: number[][][][] };
}

/** Per-theme line colour: [dark, light]. */
export interface LineStyle {
  colors: [number, number];
  opacity: [number, number];
}

export function geojsonToLineSegments(
  geojson: GeoJSON,
  radius: number,
): LineSegments<BufferGeometry, LineBasicMaterial> {
  const positions: number[] = [];
  const a = new Vector3();
  const b = new Vector3();

  // Skip antimeridian-crossing pairs so we don't draw the wrong way
  // around the sphere.
  function emitLineString(coords: number[][]) {
    for (let i = 0; i < coords.length - 1; i++) {
      const [lng1, lat1] = coords[i];
      const [lng2, lat2] = coords[i + 1];
      if (Math.abs(lng2 - lng1) > 180) continue;
      latLngToVec3(lat1, lng1, radius, a);
      latLngToVec3(lat2, lng2, radius, b);
      positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
  }

  for (const feature of geojson.features) {
    const g = feature.geometry;
    switch (g.type) {
      case "LineString":
        emitLineString(g.coordinates);
        break;
      case "MultiLineString":
        for (const line of g.coordinates) emitLineString(line);
        break;
      case "Polygon":
        for (const ring of g.coordinates) emitLineString(ring);
        break;
      case "MultiPolygon":
        for (const rings of g.coordinates)
          for (const ring of rings) emitLineString(ring);
        break;
    }
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));

  const material = new LineBasicMaterial({
    transparent: true,
    depthWrite: false,
  });

  const mesh = new LineSegments(geo, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  mesh.visible = false;
  return mesh;
}

export function applyLineStyle(
  material: LineBasicMaterial,
  style: LineStyle,
  theme: number,
): void {
  const i = theme > 0.5 ? 1 : 0;
  material.color = new Color(style.colors[i]);
  material.opacity = style.opacity[i];
}

/**
 * A line overlay fetched on first show. `setVisible` resolves once the
 * data is in the scene; calls made while loading settle on the last
 * requested visibility.
 */
export class LazyLineLayer {
  private mesh: LineSegments<BufferGeometry, LineBasicMaterial> | null = null;
  private loading: Promise<void> | null = null;
  private wanted = false;
  private theme = 0;

  constructor(
    private readonly load: () => Promise<LineSegments<BufferGeometry, LineBasicMaterial>>,
    private readonly addToScene: (m: LineSegments) => void,
    private readonly style: LineStyle,
  ) {}

  get visible(): boolean {
    return this.wanted;
  }

  async setVisible(v: boolean): Promise<void> {
    this.wanted = v;
    if (!this.mesh && v) {
      this.loading ??= this.load().then((m) => {
        this.mesh = m;
        applyLineStyle(m.material, this.style, this.theme);
        this.addToScene(m);
      });
      try {
        await this.loading;
      } catch (err) {
        this.loading = null;
        throw err;
      }
    }
    if (this.mesh) this.mesh.visible = this.wanted;
  }

  setTheme(t: number): void {
    this.theme = t;
    if (this.mesh) applyLineStyle(this.mesh.material, this.style, t);
  }
}
